/**
 * DraftReview
 *
 * Fetches a draft checklist by ID via React Query and presents it for review,
 * editing, and publishing. Orchestrates DraftClausesTable, DraftAddTopicDialog,
 * and DraftAddClauseDialog (all API-backed with query invalidation).
 */

import {
    ChecklistWriteErrorClassification,
    classifyChecklistWriteError,
    IApiAxiosError,
} from "@/api";
import {
    IDraftDetail,
    IDraftTopicDetail,
    IEnabledClauseSelection,
} from "@/api/clause-checklists/checklist-generation.types";
import {
    adminDraftQueryKeys,
    deleteDraft,
    DraftPublishRefreshUnconfirmedError,
    DraftUpdatePayload,
    DraftUpdateRefreshUnconfirmedError,
    removeTopicFromDraft,
    useDraft,
    usePublishDraft,
    useUpdateDraft,
} from "@/api/clause-checklists/drafts";
import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import {
    Alert,
    alpha,
    Box,
    CircularProgress,
    Paper,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
    BookOpen,
    Lightbulb,
    ListChecks,
    RefreshCw,
    Save,
    Trash2,
    Upload,
} from "lucide-react";
import { enqueueSnackbar } from "notistack";
import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import { useNavigate } from "react-router";
import { normalizeEnabledClauses } from "./checklistState";
import { DraftAddClauseDialog } from "./DraftAddClauseDialog";
import { DraftAddTopicDialog } from "./DraftAddTopicDialog";
import { DraftClausesTable } from "./DraftClausesTable";
import { DraftPublishTargets, PublishBlocker } from "./DraftPublishTargets";
import { GapAnalysis } from "./GapAnalysis";
import { RefineModal } from "./RefineModal";

const SAVE_REFUSED =
    "The draft couldn't be saved. Check the details and try again.";
const SAVE_FAILED = "The draft couldn't be saved. Try again.";
const PUBLISH_REFUSED =
    "The draft couldn't be published. Check where it will be shared and try again.";
const PUBLISH_CONFLICT =
    "The draft couldn't be published. Another checklist may already use this name, or the draft changed while publishing. Review it and try again.";
const PUBLISH_FAILED = "The draft couldn't be published. Try again.";
const LOAD_FAILED = "The draft couldn't be loaded.";
const SAVE_UNCONFIRMED = "The draft was saved, but this view couldn't refresh.";
const PUBLISH_UNCONFIRMED =
    "The checklist was published, but some views couldn't refresh.";
const TARGET_REFUSED =
    "The draft couldn't be saved. Check where it will be shared and try again.";

const saveFailure = ({ kind }: ChecklistWriteErrorClassification) => {
    switch (kind) {
        case "group_refused":
        case "organization_refused":
            return TARGET_REFUSED;
        case "failed":
            return SAVE_FAILED;
        default:
            return SAVE_REFUSED;
    }
};

const publishFailure = (error: unknown) => {
    switch (classifyChecklistWriteError(error).kind) {
        case "conflict":
            return PUBLISH_CONFLICT;
        case "validation":
            return PUBLISH_REFUSED;
        default:
            return PUBLISH_FAILED;
    }
};

const selectionKey = (selections: IEnabledClauseSelection[]) =>
    selections
        .map(
            (selection) =>
                `${selection.topic_clause_id}:${selection.document_section_type_id}`
        )
        .join(",");

const topicsKey = (topics: IDraftTopicDetail[]) =>
    topics
        .map(
            (topic) =>
                `${topic.topic_id}=${selectionKey(topic.enabled_clauses)}`
        )
        .join(";");

const mergeTopics = (
    local: IDraftTopicDetail[],
    previous: IDraftDetail,
    next: IDraftDetail
): IDraftTopicDetail[] => {
    const localTopics = new Map(local.map((topic) => [topic.topic_id, topic]));
    const previousTopics = new Map(
        previous.topics.map((topic) => [
            topic.topic_id,
            normalizeEnabledClauses(topic),
        ])
    );
    return next.topics.map((serverTopic) => {
        const localTopic = localTopics.get(serverTopic.topic_id);
        const previousTopic = previousTopics.get(serverTopic.topic_id);
        const untouched =
            localTopic &&
            previousTopic &&
            selectionKey(localTopic.enabled_clauses) ===
                selectionKey(previousTopic.enabled_clauses);
        if (!localTopic || untouched) {
            return normalizeEnabledClauses(serverTopic);
        }
        return { ...serverTopic, enabled_clauses: localTopic.enabled_clauses };
    });
};

interface DraftReviewProps {
    draftId: number;
    onReset: () => void;
    onRefine?: (refinementPrompt: string) => void;
    isRefining?: boolean;
}

export const DraftReview = ({
    draftId,
    onReset,
    onRefine,
    isRefining,
}: DraftReviewProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const {
        data: draft,
        dataUpdatedAt,
        isFetching,
        error: fetchError,
        refetch,
    } = useDraft(draftId);
    const update = useUpdateDraft(draftId);
    const publish = usePublishDraft(draftId);
    const [action, setAction] = useState<"save" | "publish" | "refine" | null>(
        null
    );
    const [failure, setFailure] = useState<string | null>(null);
    const [refusedOrganizationId, setRefusedOrganizationId] = useState<
        number | null
    >(null);
    const [refusedGroupId, setRefusedGroupId] = useState<number | null>(null);
    const [unconfirmedSave, setUnconfirmedSave] = useState<{
        draft: IDraftDetail;
        readAt: number;
    } | null>(null);
    const blockerId = useId();

    // Editable fields (synced from query data)
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [organizationId, setOrganizationId] = useState<number | null>(null);
    const [groupId, setGroupId] = useState<number | null>(null);
    const [topics, setTopics] = useState<IDraftTopicDetail[]>([]);
    const [initialized, setInitialized] = useState(false);
    const serverDraft = useRef<IDraftDetail | null>(null);

    const adopt = useCallback((next: IDraftDetail) => {
        const previous = serverDraft.current;
        serverDraft.current = next;
        if (!previous) {
            setName(next.name);
            setDescription(next.description);
            setOrganizationId(next.organization_id);
            setGroupId(next.draft_publication_group_id);
            setTopics(next.topics.map(normalizeEnabledClauses));
            setInitialized(true);
            return;
        }
        if (previous === next) {
            return;
        }
        setName((local) => (local === previous.name ? next.name : local));
        setDescription((local) =>
            local === previous.description ? next.description : local
        );
        setOrganizationId((local) =>
            local === previous.organization_id ? next.organization_id : local
        );
        setGroupId((local) =>
            local === previous.draft_publication_group_id
                ? next.draft_publication_group_id
                : local
        );
        setTopics((local) => mergeTopics(local, previous, next));
    }, []);

    useEffect(() => {
        if (draft) {
            adopt(draft);
        }
    }, [draft, adopt]);

    const confirmedDraft =
        unconfirmedSave?.readAt === dataUpdatedAt
            ? unconfirmedSave.draft
            : null;
    const current = confirmedDraft ?? draft;

    // Dialog state
    const [discardOpen, setDiscardOpen] = useState(false);
    const [refineOpen, setRefineOpen] = useState(false);
    const [addTopicOpen, setAddTopicOpen] = useState(false);
    const [addClauseOpen, setAddClauseOpen] = useState(false);

    // Derived stats
    const totalClauses = useMemo(
        () => topics.reduce((sum, t) => sum + t.enabled_clauses.length, 0),
        [topics]
    );

    const activeTopicCount = useMemo(
        () => topics.filter((t) => t.enabled_clauses.length > 0).length,
        [topics]
    );

    const gapSuggestions = current?.gap_suggestions?.items ?? [];
    const hasGaps = gapSuggestions.length > 0;

    const excludeTopicIds = useMemo(
        () => topics.map((t) => t.topic_id),
        [topics]
    );

    const buildPayload = (): DraftUpdatePayload => ({
        name: name.trim(),
        description: description.trim(),
        ...(organizationId !== current?.organization_id && {
            organization_id: organizationId,
        }),
        ...(groupId !== current?.draft_publication_group_id && {
            draft_publication_group_id: groupId,
        }),
        updates: topics
            .filter((t) => t.enabled_clauses.length > 0)
            .map((t) => ({
                topic_id: t.topic_id,
                enabled_clauses: t.enabled_clauses,
            })),
    });

    // --- Mutations ---

    const removeTopicMutation = useMutation({
        mutationFn: (topicId: number) => removeTopicFromDraft(draftId, topicId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: adminDraftQueryKeys.detail(draftId),
            });
        },
        onError: (err: Error) => {
            enqueueSnackbar(err.message || "Failed to remove topic", {
                variant: "error",
            });
        },
    });

    const discardMutation = useMutation({
        mutationFn: () => deleteDraft(draftId),
        onSuccess: () => onReset(),
        onError: (err: Error) => {
            enqueueSnackbar(err.message || "Failed to discard draft", {
                variant: "error",
            });
        },
    });

    const saveDraft = async () => {
        const payload = buildPayload();
        try {
            await update.mutateAsync(payload);
            return "saved";
        } catch (error) {
            if (error instanceof DraftUpdateRefreshUnconfirmedError) {
                adopt(error.confirmedDraft);
                setUnconfirmedSave({
                    draft: error.confirmedDraft,
                    readAt:
                        queryClient.getQueryState(
                            adminDraftQueryKeys.detail(draftId)
                        )?.dataUpdatedAt ?? 0,
                });
                return "unconfirmed";
            }
            const failure = classifyChecklistWriteError(error, payload);
            if (failure.kind === "group_refused") {
                setRefusedGroupId(failure.targetId);
            } else if (failure.kind === "organization_refused") {
                setRefusedOrganizationId(failure.targetId);
            }
            setFailure(saveFailure(failure));
            setAction(null);
            return "failed";
        }
    };

    const startAttempt = (next: "save" | "publish" | "refine") => {
        setFailure(null);
        setRefusedOrganizationId(null);
        setRefusedGroupId(null);
        setAction(next);
    };

    const handleSave = async () => {
        startAttempt("save");
        const saved = await saveDraft();
        if (saved === "saved") {
            navigate("/admin/checklists");
        } else if (saved === "unconfirmed") {
            setAction(null);
        }
    };

    const handlePublish = async () => {
        startAttempt("publish");
        if ((await saveDraft()) === "failed") {
            return;
        }
        try {
            const result = await publish.mutateAsync();
            navigate(`/admin/checklists/${result.id}/details`);
        } catch (error) {
            if (error instanceof DraftPublishRefreshUnconfirmedError) {
                enqueueSnackbar(PUBLISH_UNCONFIRMED, { variant: "warning" });
                navigate(
                    `/admin/checklists/${error.confirmedPublication.id}/details`
                );
                return;
            }
            setFailure(publishFailure(error));
            setAction(null);
            void refetch();
        }
    };

    const hasUnsavedEdits = () =>
        !!current &&
        (name !== current.name ||
            description !== current.description ||
            organizationId !== current.organization_id ||
            groupId !== current.draft_publication_group_id ||
            topicsKey(topics) !==
                topicsKey(current.topics.map(normalizeEnabledClauses)));

    const handleRefine = async (refinementPrompt: string) => {
        setRefineOpen(false);
        if (hasUnsavedEdits()) {
            startAttempt("refine");
            if ((await saveDraft()) === "failed") {
                return;
            }
            setAction(null);
        }
        onRefine?.(refinementPrompt);
    };

    const handleDiscard = () => {
        setDiscardOpen(false);
        discardMutation.mutate();
    };

    const isBusy =
        action !== null || removeTopicMutation.isPending || !!isRefining;

    const readStatus = (fetchError as IApiAxiosError | null)?.response?.status;
    const readAccessLost =
        readStatus === 401 || readStatus === 403 || readStatus === 404;

    if (fetchError && !isFetching && (!draft || readAccessLost)) {
        return (
            <Alert
                severity="error"
                action={
                    <ActionButton variant="text" onClick={() => void refetch()}>
                        Try again
                    </ActionButton>
                }
            >
                {LOAD_FAILED}
            </Alert>
        );
    }

    if (!current || !initialized) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 8,
                }}
            >
                <CircularProgress size={32} />
            </Box>
        );
    }

    const savedGroupUnavailable =
        current.draft_publication_group_status === "unavailable" &&
        groupId === current.draft_publication_group_id;
    const blocker: PublishBlocker | null = savedGroupUnavailable
        ? "group_unavailable"
        : organizationId === null && groupId === null
          ? "no_destination"
          : null;

    return (
        <Stack spacing={3}>
            {fetchError && !isFetching && !confirmedDraft && (
                <Alert
                    severity="warning"
                    role="status"
                    action={
                        <ActionButton
                            variant="text"
                            onClick={() => void refetch()}
                        >
                            Try again
                        </ActionButton>
                    }
                >
                    {LOAD_FAILED} The displayed draft may be out of date.
                </Alert>
            )}
            {/* Name & Description */}
            <Paper
                variant="outlined"
                sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
            >
                <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={600}>
                        Review Draft
                    </Typography>

                    <TextField
                        label="Checklist Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        disableFocusShadow={false}
                    />

                    <TextField
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        disableFocusShadow={false}
                    />

                    <DraftPublishTargets
                        draft={current}
                        organizationId={organizationId}
                        onOrganizationChange={setOrganizationId}
                        groupId={groupId}
                        onGroupChange={setGroupId}
                        organizationRefused={
                            refusedOrganizationId !== null &&
                            refusedOrganizationId === organizationId
                        }
                        groupRefused={
                            refusedGroupId !== null &&
                            refusedGroupId === groupId
                        }
                        blocker={blocker}
                        blockerId={blockerId}
                        disabled={isBusy}
                    />
                </Stack>

                {/* Summary stats */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr 1fr",
                            sm: `repeat(${hasGaps ? 3 : 2}, 1fr)`,
                        },
                        gap: { xs: 1.5, sm: 2 },
                        mt: 2,
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.5, sm: 2 },
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            border: "1px solid",
                            borderColor: alpha(
                                theme.palette.primary.main,
                                0.12
                            ),
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 1.5, sm: 2 },
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: 36, sm: 44 },
                                height: { xs: 36, sm: 44 },
                                borderRadius: "12px",
                                bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.08
                                ),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <BookOpen
                                size={18}
                                color={theme.palette.primary.main}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="h5"
                                fontWeight={700}
                                color="primary"
                                sx={{
                                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                                    lineHeight: 1.2,
                                }}
                            >
                                {activeTopicCount}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                            >
                                Topics
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            px: { xs: 1.5, sm: 2.5 },
                            py: { xs: 1.5, sm: 2 },
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.success.main, 0.04),
                            border: "1px solid",
                            borderColor: alpha(
                                theme.palette.success.main,
                                0.12
                            ),
                            display: "flex",
                            alignItems: "center",
                            gap: { xs: 1.5, sm: 2 },
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: 36, sm: 44 },
                                height: { xs: 36, sm: 44 },
                                borderRadius: "12px",
                                bgcolor: alpha(
                                    theme.palette.success.main,
                                    0.08
                                ),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <ListChecks
                                size={18}
                                color={theme.palette.success.main}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="h5"
                                fontWeight={700}
                                color="success.main"
                                sx={{
                                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                                    lineHeight: 1.2,
                                }}
                            >
                                {totalClauses}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                            >
                                Clauses Selected
                            </Typography>
                        </Box>
                    </Box>

                    {hasGaps && (
                        <Box
                            sx={{
                                px: { xs: 1.5, sm: 2.5 },
                                py: { xs: 1.5, sm: 2 },
                                borderRadius: 2,
                                bgcolor: alpha(
                                    theme.palette.warning.main,
                                    0.04
                                ),
                                border: "1px solid",
                                borderColor: alpha(
                                    theme.palette.warning.main,
                                    0.12
                                ),
                                display: "flex",
                                alignItems: "center",
                                gap: { xs: 1.5, sm: 2 },
                                gridColumn: { xs: "1 / -1", sm: "auto" },
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: 36, sm: 44 },
                                    height: { xs: 36, sm: 44 },
                                    borderRadius: "12px",
                                    bgcolor: alpha(
                                        theme.palette.warning.main,
                                        0.08
                                    ),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <Lightbulb
                                    size={18}
                                    color={theme.palette.warning.main}
                                />
                            </Box>
                            <Box>
                                <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    color="warning.main"
                                    sx={{
                                        fontSize: {
                                            xs: "1.25rem",
                                            sm: "1.5rem",
                                        },
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {gapSuggestions.length}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 500 }}
                                >
                                    New Clause Suggestions
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Interactive topic accordions */}
            <Paper
                variant="outlined"
                sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
            >
                <DraftClausesTable
                    topics={topics}
                    onChange={setTopics}
                    onAddTopic={() => setAddTopicOpen(true)}
                    onAddClause={() => setAddClauseOpen(true)}
                    onRemoveTopic={(topicId) =>
                        removeTopicMutation.mutateAsync(topicId)
                    }
                />
            </Paper>

            {/* Gap Analysis */}
            {hasGaps && (
                <Paper
                    variant="outlined"
                    sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
                >
                    <GapAnalysis
                        draftId={draftId}
                        suggestions={gapSuggestions}
                    />
                </Paper>
            )}

            {/* Refine modal */}
            <RefineModal
                open={refineOpen && !isRefining}
                onClose={() => setRefineOpen(false)}
                onSubmit={(refinementPrompt) =>
                    void handleRefine(refinementPrompt)
                }
            />

            {confirmedDraft && (
                <Alert
                    severity="warning"
                    role="status"
                    action={
                        <ActionButton
                            variant="text"
                            disabled={isFetching}
                            onClick={() => void refetch()}
                        >
                            Reload
                        </ActionButton>
                    }
                >
                    {SAVE_UNCONFIRMED}
                </Alert>
            )}
            {failure && <Alert severity="error">{failure}</Alert>}

            {/* Action bar */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="space-between"
            >
                <Stack direction="row" spacing={1.5}>
                    <ActionButton
                        variant="text"
                        color="error"
                        startIcon={<Trash2 size={16} />}
                        onClick={() => setDiscardOpen(true)}
                        disabled={isBusy}
                    >
                        Discard
                    </ActionButton>
                    {onRefine && (
                        <ActionButton
                            variant="outlined"
                            startIcon={<RefreshCw size={14} />}
                            onClick={() => setRefineOpen(!refineOpen)}
                            disabled={isBusy}
                        >
                            Refine
                        </ActionButton>
                    )}
                </Stack>

                <Stack direction="row" spacing={1.5}>
                    <ActionButton
                        variant="text"
                        startIcon={<Save size={16} />}
                        onClick={() => void handleSave()}
                        disabled={isBusy || !name.trim()}
                    >
                        {action === "save" ? "Saving\u2026" : "Save Draft"}
                    </ActionButton>
                    <ActionButton
                        variant="contained"
                        startIcon={<Upload size={16} />}
                        onClick={() => {
                            if (blocker === null) {
                                void handlePublish();
                            }
                        }}
                        disabled={isBusy || !name.trim() || totalClauses === 0}
                        aria-disabled={blocker !== null || undefined}
                        aria-describedby={
                            blocker !== null ? blockerId : undefined
                        }
                    >
                        {action === "publish"
                            ? "Creating\u2026"
                            : "Create Checklist"}
                    </ActionButton>
                </Stack>
            </Stack>

            {/* Discard confirmation dialog */}
            <Modal
                open={discardOpen}
                onClose={() => setDiscardOpen(false)}
                title="Discard Draft"
                maxWidth="xs"
                showCancel
                actions={[
                    {
                        label: "Discard",
                        onClick: handleDiscard,
                        variant: "contained",
                        color: "error",
                        disabled: discardMutation.isPending,
                    },
                ]}
            >
                <Typography>
                    This will permanently delete the draft checklist &ldquo;
                    {current.name}&rdquo;. This action cannot be undone.
                </Typography>
            </Modal>

            {/* Add Topic Dialog */}
            <DraftAddTopicDialog
                open={addTopicOpen}
                onClose={() => setAddTopicOpen(false)}
                draftId={draftId}
                excludeTopicIds={excludeTopicIds}
            />

            {/* Add Clause Dialog */}
            <DraftAddClauseDialog
                open={addClauseOpen}
                onClose={() => setAddClauseOpen(false)}
                draftId={draftId}
                excludeTopicIds={excludeTopicIds}
            />
        </Stack>
    );
};
